/**
 * BulkActionBar — toolbar inferior fija que aparece cuando hay items
 * seleccionados en HiddenItemsTab o DubiousItemsTab.
 *
 * v1: solo expone las acciones de cada tab (hide/unhide/recatalog). El resto
 * (editar marca bulk, subcat bulk, CSV, reportar TomaDatos) quedan stubbed
 * como botones disabled con tooltip "Próximamente" — diferidos a v2.
 */
import React from 'react';
import { Eye, EyeOff, RefreshCw, Download, Flag, Pencil, X } from 'lucide-react';

export interface BulkAction {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  variant: 'primary' | 'warning' | 'success' | 'neutral' | 'danger';
  onClick: () => void;
  disabled?: boolean;
  /** Si está disabled, tooltip explica por qué (ej "Próximamente"). */
  disabledHint?: string;
}

interface Props {
  selectedCount: number;
  onClear: () => void;
  actions: BulkAction[];
}

const VARIANT_CLASS: Record<BulkAction['variant'], string> = {
  primary: 'bg-blue-600 hover:bg-blue-700 text-white',
  warning: 'bg-amber-600 hover:bg-amber-700 text-white',
  success: 'bg-emerald-600 hover:bg-emerald-700 text-white',
  neutral: 'bg-gray-200 hover:bg-gray-300 text-gray-800',
  danger: 'bg-red-600 hover:bg-red-700 text-white',
};

const BulkActionBar: React.FC<Props> = ({ selectedCount, onClear, actions }) => {
  if (selectedCount === 0) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg px-4 py-2 flex items-center gap-2 z-40">
      <span className="text-sm font-medium text-gray-700 tabular-nums">
        {selectedCount.toLocaleString('es-AR')} seleccionado{selectedCount === 1 ? '' : 's'}
      </span>
      <span className="text-gray-300 mx-1">·</span>
      <div className="flex flex-wrap items-center gap-1.5">
        {actions.map((a) => {
          const Icon = a.icon;
          const cls = `inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded transition-colors ${VARIANT_CLASS[a.variant]} ${a.disabled ? 'opacity-50 cursor-not-allowed' : ''}`;
          return (
            <button
              key={a.id}
              type="button"
              onClick={a.disabled ? undefined : a.onClick}
              disabled={a.disabled}
              title={a.disabled ? a.disabledHint || 'Deshabilitado' : a.label}
              className={cls}
            >
              <Icon className="w-3.5 h-3.5" />
              {a.label}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        onClick={onClear}
        className="ml-auto p-1 text-gray-500 hover:text-gray-700"
        title="Limpiar selección"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// Re-export icons útiles para que las tabs hijas no las re-importen
export { Eye, EyeOff, RefreshCw, Download, Flag, Pencil };

export default BulkActionBar;
